<template>
  <div>
    <b-overlay :show="loading" spinner-variant="primary">
      <b-card>
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0">Drivers Shifts</h4>
          <b-button variant="primary" @click="createNewShift">
            <feather-icon icon="PlusIcon" class="mr-50" />
            Create New Shift
          </b-button>
        </div>

        <!-- Table -->
        <b-table
          :items="shifts"
          :fields="tableColumns"
          responsive
          show-empty
          empty-text="No shifts found"
          class="position-relative"
        >
          <!-- Column: Name -->
          <template #cell(name)="data">
            <div class="d-flex flex-column">
              <span class="font-weight-bold">{{ data.item.name }}</span>
            </div>
          </template>

          <!-- Column: Days -->
          <template #cell(days_display)="data">
            <b-badge variant="light-primary" class="text-capitalize">
              {{ data.item.days_display }}
            </b-badge>
          </template>

          <template #cell(created_by)="data">
            <span class="text-muted">{{ data.item.created_by }}</span>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body"
                />
              </template>

              <b-dropdown-item @click="editShift(data.item)">
                <feather-icon icon="EditIcon" class="mr-50" />
                Edit
              </b-dropdown-item>

              <b-dropdown-item @click="deleteShift(data.item)">
                <feather-icon icon="TrashIcon" class="mr-50" />
                Delete
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
      </b-card>
    </b-overlay>

    <!-- Delete Confirmation Modal -->
    <b-modal id="delete-shift-modal" title="Delete Shift" hide-footer>
      <p v-if="selectedShift">
        Are you sure you want to delete <strong>{{ selectedShift.name }}</strong
        >?
      </p>
      <p class="text-muted">This action cannot be undone.</p>

      <div class="d-flex justify-content-end mt-3">
        <b-button
          class="mr-2"
          variant="outline-secondary"
          @click="cancelDelete"
        >
          Cancel
        </b-button>
        <b-button variant="danger" :disabled="deleting" @click="confirmDelete">
          <b-spinner v-if="deleting" small />
          {{ deleting ? "Deleting..." : "Delete" }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BTable,
  BOverlay,
  BDropdown,
  BDropdownItem,
  BBadge,
  BModal,
  BSpinner,
} from "bootstrap-vue";
import { ref, onMounted } from "@vue/composition-api";
import axiosIns from "@/libs/axios";
import {
  errorNotification,
  successNotification,
} from "@/utils/toast-notifications";

export default {
  components: {
    BCard,
    BButton,
    BTable,
    BOverlay,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BBadge,
    BModal,
  },
  setup(_, { root }) {
    const loading = ref(false);
    const shifts = ref([]);
    const selectedShift = ref(null);
    const deleting = ref(false);

    const tableColumns = [
      { key: "name", label: "Shift Name" },
      { key: "days_display", label: "Days" },
      { key: "created_by", label: "Created By" },
      { key: "actions", label: "Actions" },
    ];

    const fetchShifts = async () => {
      try {
        loading.value = true;
        const response = await axiosIns.get("/drivers/shifts");
        shifts.value = response.data.data || [];
      } catch (error) {
        console.error("Error fetching shifts:", error);
        errorNotification("Failed to fetch shifts");
      } finally {
        loading.value = false;
      }
    };

    const createNewShift = () => {
      root.$router.push({ name: "drivers-shifts-create" });
    };

    const editShift = (shift) => {
      root.$router.push({
        name: "drivers-shifts-edit",
        params: { id: shift.id },
      });
    };

    const deleteShift = (shift) => {
      selectedShift.value = shift;
      root.$bvModal.show("delete-shift-modal");
    };

    const confirmDelete = async () => {
      if (!selectedShift.value) return;

      try {
        deleting.value = true;
        await axiosIns.delete(`/drivers/shifts/${selectedShift.value.id}`);
        successNotification("Shift deleted successfully");
        root.$bvModal.hide("delete-shift-modal");
        await fetchShifts();
      } catch (error) {
        console.error("Error deleting shift:", error);
        errorNotification("Failed to delete shift");
      } finally {
        deleting.value = false;
        selectedShift.value = null;
      }
    };

    const cancelDelete = () => {
      root.$bvModal.hide("delete-shift-modal");
      selectedShift.value = null;
    };

    const formatTime = (timeString) => {
      if (!timeString) return "-";
      const date = new Date(timeString);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    };

    onMounted(() => {
      fetchShifts();
    });

    return {
      loading,
      shifts,
      tableColumns,
      selectedShift,
      deleting,
      createNewShift,
      editShift,
      deleteShift,
      confirmDelete,
      cancelDelete,
      formatTime,
    };
  },
};
</script>

<style scoped>
.text-body {
  color: #6e6b7b !important;
}
</style>
